/**
 * Step-up verification page — /step-up (AUTH-134)
 *
 * Reached after a federated (Google) or password sign-in, when the consuming
 * service's `createJWT` call returned `STEP_UP_REQUIRED`. Lists the user's
 * enrolled factors and lets them pick one to verify. On success, the after-
 * hook in api/src/auth.ts flips `auth_session.mfaSatisfied=true` (AUTH-131)
 * and the user is redirected back to the original destination.
 *
 * Distinct from /2fa, which is Better Auth's built-in redirect target after
 * a password sign-in for users with `twoFactorEnabled=true`. /step-up is the
 * comprehensive entrypoint that also covers passkey-only users (whose
 * twoFactor plugin doesn't gate them) and the post-OAuth case.
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "@tanstack/react-form";
import { Button } from "@better-bookkeeping/ui/button";
import { Input } from "@better-bookkeeping/ui/input";
import { Alert, AlertDescription } from "@better-bookkeeping/ui/alert";
import { AlertCircle, Fingerprint, Smartphone, Mail, Key } from "lucide-react";
import { useEffect, useState } from "react";
import { z } from "zod";
import { authClient, orpcClient } from "@/lib/auth-client";
import { safePostAuthRedirect } from "@/lib/redirect-utils";
import { navigateToFinalDestination } from "@/lib/post-auth-redirect";
import { useRuntimeConfig } from "@/lib/runtime-config";
import { classifyPasskeyError } from "@/lib/passkey-errors";
import { AuthLoading } from "@/components/auth-loading";
import { OtpDigits } from "@/components/otp-digits";
import { DuplicateAccountWarningModal } from "@/components/duplicate-account-warning-modal";
import { preparePendingInvitationAuth, withInviteTokenSearch } from "@/lib/pending-pricing-invite";

type Mode = "picker" | "totp" | "otp" | "recovery";

export const Route = createFileRoute("/step-up")({
  validateSearch: withInviteTokenSearch({
    redirect_uri: z.string().optional(),
  }).parse,
  component: StepUpPage,
});

function StepUpPage() {
  const { redirect_uri } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const runtimeConfig = useRuntimeConfig();
  const [mode, setMode] = useState<Mode>("picker");
  const [error, setError] = useState<string | null>(null);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [otpSending, setOtpSending] = useState(false);
  const [otpSent, setOtpSent] = useState(false);

  const sessionQuery = useQuery({
    queryKey: ["auth", "gateway-session"],
    queryFn: () => orpcClient.auth.getSession(),
    retry: false,
  });

  const passkeysQuery = useQuery({
    queryKey: ["auth", "passkeys"],
    queryFn: () => orpcClient.auth.listPasskeys(),
    retry: false,
    enabled: !!sessionQuery.data,
  });

  const otpStatusQuery = useQuery({
    queryKey: ["auth", "otp-delivery-status"],
    queryFn: () => orpcClient.auth.getOtpDeliveryStatus(),
    retry: false,
    enabled: !!sessionQuery.data,
  });

  // AUTH-154: warn duplicate-flagged accounts BEFORE the step-up challenge.
  // A session exists here (mfaSatisfied=false), so the standard check works.
  // skipDuplicateCheck is only passed downstream when this screen actually
  // resolved the warning (not flagged, or modal acknowledged) — paths that
  // never render the modal (e.g. arriving with mfaSatisfied already true)
  // fall through to the post-auth safety net.
  const duplicateQuery = useQuery({
    queryKey: ["auth", "duplicate-check"],
    queryFn: () => orpcClient.auth.checkDuplicate(),
    retry: false,
    enabled: !!sessionQuery.data,
  });
  const [duplicateAcked, setDuplicateAcked] = useState(false);
  const showDuplicateWarning = !!duplicateQuery.data?.isDuplicate && !duplicateAcked;
  const duplicateResolved = duplicateQuery.isSuccess && !showDuplicateWarning;

  const session = sessionQuery.data;
  const passkeys = passkeysQuery.data ?? [];
  const twoFactorEnabled = session?.user.twoFactorEnabled ?? false;

  // Already MFA-satisfied — go straight to destination. Covers the case
  // where a user lands here on a session that already passed step-up
  // (e.g., bookmarked /step-up, browser back button).
  useEffect(() => {
    if (session?.session.mfaSatisfied) {
      const target = safePostAuthRedirect(redirect_uri, runtimeConfig) ?? "/";
      void navigateToFinalDestination({
        target,
        navigate,
        runtimeConfig,
        skipDuplicateCheck: duplicateResolved,
      });
    }
  }, [session, redirect_uri, runtimeConfig, navigate, duplicateResolved]);

  // Not authenticated — bounce to login, preserving redirect_uri.
  useEffect(() => {
    if (sessionQuery.isFetched && !session) {
      navigate({
        to: "/login",
        search: { redirect_uri },
      });
    }
  }, [sessionQuery.isFetched, session, navigate, redirect_uri]);

  function redirectToDestination() {
    queryClient.setQueryData<typeof session>(["auth", "gateway-session"], (current) =>
      current
        ? {
            ...current,
            session: { ...current.session, mfaSatisfied: true },
          }
        : current,
    );

    const target = safePostAuthRedirect(redirect_uri, runtimeConfig) ?? "/";
    void navigateToFinalDestination({
      target,
      navigate,
      runtimeConfig,
      skipDuplicateCheck: duplicateResolved,
    });
  }

  async function verifyWithPasskey() {
    try {
      setPasskeyLoading(true);
      setError(null);
      await preparePendingInvitationAuth(undefined);
      const { error: pkError } = await authClient.signIn.passkey();
      if (pkError) {
        setError(classifyPasskeyError(pkError).message);
        return;
      }
      redirectToDestination();
    } catch (err) {
      setError(classifyPasskeyError(err).message);
    } finally {
      setPasskeyLoading(false);
    }
  }

  async function sendOtp() {
    try {
      setError(null);
      setOtpSending(true);
      if (!otpDeliveryMethod) {
        setOtpSent(false);
        setError("This account uses an authenticator app. Pick that method instead.");
        return;
      }

      const { error: sendError } = await authClient.twoFactor.sendOtp();
      if (sendError) {
        setOtpSent(false);
        setError(`We couldn't send a code via ${otpDeliveryLabel}. Try again.`);
        return;
      }
      setOtpSent(true);
    } catch (err) {
      console.error("OTP send failed:", err);
      setOtpSent(false);
      setError(`We couldn't send a code via ${otpDeliveryLabel}. Try again.`);
    } finally {
      setOtpSending(false);
    }
  }

  function startOtpFlow() {
    setError(null);
    setOtpSent(false);
    otpForm.reset();
    setMode("otp");
    void sendOtp();
  }

  const totpForm = useForm({
    defaultValues: { code: "" },
    onSubmit: async ({ value }) => {
      try {
        setError(null);
        const { error: verifyError } = await authClient.twoFactor.verifyTotp({
          code: value.code,
        });
        if (verifyError) {
          setError("That code didn't match. Try again — codes refresh every 30 seconds.");
          return;
        }
        redirectToDestination();
      } catch (err) {
        setError(err instanceof Error ? err.message : "Verification failed");
      }
    },
  });

  const otpForm = useForm({
    defaultValues: { code: "" },
    onSubmit: async ({ value }) => {
      try {
        setError(null);
        const { error: verifyError } = await authClient.twoFactor.verifyOtp({
          code: value.code,
        });
        if (verifyError) {
          setError("That code didn't match. Codes expire after a few minutes — try resending.");
          return;
        }
        redirectToDestination();
      } catch (err) {
        setError(err instanceof Error ? err.message : "Verification failed");
      }
    },
  });

  const recoveryForm = useForm({
    defaultValues: { code: "" },
    onSubmit: async ({ value }) => {
      try {
        setError(null);
        const { error: verifyError } = await authClient.twoFactor.verifyBackupCode({
          code: value.code,
        });
        if (verifyError) {
          setError("That recovery code didn't work. Each one is single-use — try a different one.");
          return;
        }
        redirectToDestination();
      } catch (err) {
        setError(err instanceof Error ? err.message : "Verification failed");
      }
    },
  });

  if (
    sessionQuery.isLoading ||
    (session && passkeysQuery.isLoading) ||
    (session && duplicateQuery.isLoading) ||
    (session && twoFactorEnabled && otpStatusQuery.isLoading)
  ) {
    return <AuthLoading data-testid="step-up-loading" />;
  }

  // AUTH-136: render the loading placeholder during the brief window
  // between "we know we need to redirect" and the navigation effect
  // firing. Without this, the picker UI renders for a frame before
  // the redirect kicks in (the AUTH-136 flicker).
  if (!session || session.session.mfaSatisfied) {
    return <AuthLoading data-testid="step-up-redirecting-loading" />;
  }

  const hasPasskey = passkeys.length > 0;
  const otpDeliveryMethod = otpStatusQuery.data?.method ?? null;
  const hasTotp = twoFactorEnabled && otpDeliveryMethod === null;
  const hasDeliveryOtp = twoFactorEnabled && otpDeliveryMethod !== null;
  const hasSecondFactor = hasPasskey || twoFactorEnabled;
  const isSmsDelivery = otpDeliveryMethod === "SMS" && otpStatusQuery.data?.smsAvailable;
  const otpDeliveryLabel = isSmsDelivery ? "SMS" : "email";
  const OtpDeliveryIcon = isSmsDelivery ? Smartphone : Mail;

  return (
    <div className="space-y-6">
      <DuplicateAccountWarningModal
        open={showDuplicateWarning}
        canonicalEmail={duplicateQuery.data?.duplicateOfEmail ?? null}
        currentEmail={session.user.email}
        onProceed={() => setDuplicateAcked(true)}
        onUseOtherAccount={() => navigate({ to: "/login", search: { redirect_uri } })}
        data-testid="step-up-duplicate-account-dialog"
      />
      <div className="text-center space-y-2">
        {/* Carries the mode — a test reads this to know which challenge it got. */}
        <h2 className="text-2xl font-semibold tracking-tight" data-testid="step-up-title">
          {mode === "picker" && "Confirm it's you"}
          {mode === "totp" && "Enter your authenticator code"}
          {mode === "otp" && "Check your messages"}
          {mode === "recovery" && "Use a recovery code"}
        </h2>
        <p className="text-sm text-muted-foreground" data-testid="step-up-subtitle">
          {mode === "picker" && "Pick a way to verify so we can keep going."}
          {mode === "totp" && "Enter the 6-digit code from your authenticator app."}
          {mode === "otp" &&
            (otpSent
              ? "We sent a 6-digit code. Enter it below."
              : `We'll send a 6-digit code via ${otpDeliveryLabel}.`)}
          {mode === "recovery" && "Enter one of the backup codes you saved during setup."}
        </p>
      </div>

      {error && (
        <Alert variant="destructive" data-testid="step-up-error">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {mode === "picker" && (
        <div className="space-y-3">
          {hasPasskey && (
            <Button
              type="button"
              className="w-full"
              disabled={passkeyLoading}
              onClick={verifyWithPasskey}
              data-testid="step-up-passkey-button"
            >
              <Fingerprint className="mr-2 h-4 w-4" />
              {passkeyLoading ? "Waiting for passkey…" : "Use a passkey"}
            </Button>
          )}
          {hasTotp && (
            <Button
              type="button"
              variant={hasPasskey ? "outline" : "default"}
              className="w-full"
              onClick={() => {
                setError(null);
                setMode("totp");
              }}
              data-testid="step-up-use-authenticator-button"
            >
              <Key className="mr-2 h-4 w-4" />
              Use authenticator app
            </Button>
          )}
          {hasDeliveryOtp && (
            <Button
              type="button"
              variant={hasPasskey ? "outline" : "default"}
              className="w-full"
              onClick={startOtpFlow}
              disabled={otpSending}
              data-testid="step-up-use-otp-button"
            >
              <OtpDeliveryIcon className="mr-2 h-4 w-4" />
              {otpSending ? "Sending…" : `Send a code via ${otpDeliveryLabel}`}
            </Button>
          )}
          {!hasSecondFactor && (
            <Alert data-testid="step-up-no-factor-empty-state">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                No security method is enrolled on this account.{" "}
                <a
                  href="mailto:support@withvisor.com?subject=Account%20recovery"
                  className="underline underline-offset-2"
                  data-testid="step-up-support-link"
                >
                  Contact support
                </a>{" "}
                and we'll help you back in.
              </AlertDescription>
            </Alert>
          )}
          {hasSecondFactor && (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setError(null);
                setMode("recovery");
              }}
              className="w-full"
              data-testid="step-up-use-recovery-button"
            >
              <Key className="mr-2 h-4 w-4" />
              Lost access? Use a recovery code
            </Button>
          )}
        </div>
      )}

      {mode === "totp" && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            totpForm.handleSubmit();
          }}
          className="space-y-6"
          data-testid="step-up-totp-form"
        >
          <totpForm.Field
            name="code"
            validators={{
              onChange: z.string().length(6, "Code must be 6 digits").regex(/^\d+$/, "Digits only"),
            }}
            children={(field) => (
              <div className="space-y-2">
                <OtpDigits
                  value={field.state.value}
                  onChange={field.handleChange}
                  data-testid="step-up-totp-code-input"
                />
                {field.state.meta.errors.length > 0 && field.state.value.length > 0 && (
                  <p
                    className="text-sm text-destructive text-center"
                    data-testid="step-up-totp-error"
                  >
                    {typeof field.state.meta.errors[0] === "string"
                      ? field.state.meta.errors[0]
                      : field.state.meta.errors[0]?.message}
                  </p>
                )}
              </div>
            )}
          />
          <totpForm.Subscribe
            selector={(state) => [state.canSubmit, state.isSubmitting]}
            children={([canSubmit, isSubmitting]) => (
              <Button
                type="submit"
                disabled={!canSubmit || isSubmitting}
                className="w-full"
                data-testid="step-up-totp-submit-button"
              >
                {isSubmitting ? "Verifying…" : "Verify"}
              </Button>
            )}
          />
        </form>
      )}

      {mode === "otp" && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            otpForm.handleSubmit();
          }}
          className="space-y-6"
          data-testid="step-up-otp-form"
        >
          {!otpSent ? (
            <Button
              type="button"
              onClick={sendOtp}
              disabled={otpSending}
              className="w-full"
              data-testid="step-up-send-code-button"
            >
              {otpSending ? "Sending…" : "Send code"}
            </Button>
          ) : (
            <>
              <otpForm.Field
                name="code"
                validators={{
                  onChange: z
                    .string()
                    .length(6, "Code must be 6 digits")
                    .regex(/^\d+$/, "Digits only"),
                }}
                children={(field) => (
                  <div className="space-y-2">
                    <OtpDigits
                      value={field.state.value}
                      onChange={field.handleChange}
                      data-testid="step-up-otp-code-input"
                    />
                    {field.state.meta.errors.length > 0 && field.state.value.length > 0 && (
                      <p
                        className="text-sm text-destructive text-center"
                        data-testid="step-up-otp-error"
                      >
                        {typeof field.state.meta.errors[0] === "string"
                          ? field.state.meta.errors[0]
                          : field.state.meta.errors[0]?.message}
                      </p>
                    )}
                  </div>
                )}
              />
              <otpForm.Subscribe
                selector={(state) => [state.canSubmit, state.isSubmitting]}
                children={([canSubmit, isSubmitting]) => (
                  <Button
                    type="submit"
                    disabled={!canSubmit || isSubmitting}
                    className="w-full"
                    data-testid="step-up-otp-submit-button"
                  >
                    {isSubmitting ? "Verifying…" : "Verify"}
                  </Button>
                )}
              />
              <button
                type="button"
                onClick={() => {
                  setOtpSent(false);
                  otpForm.reset();
                  void sendOtp();
                }}
                disabled={otpSending}
                className="block w-full text-center text-xs text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
                data-testid="step-up-resend-code-button"
              >
                Didn't get it? Resend
              </button>
            </>
          )}
        </form>
      )}

      {mode === "recovery" && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            recoveryForm.handleSubmit();
          }}
          className="space-y-6"
          data-testid="step-up-recovery-form"
        >
          <recoveryForm.Field
            name="code"
            validators={{
              onChange: z.string().min(1, "Enter a recovery code"),
            }}
            children={(field) => (
              <Input
                type="text"
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value.trim())}
                placeholder="abcd-efgh-1234"
                className="text-lg text-center font-mono tracking-wider"
                autoFocus
                data-testid="step-up-recovery-code-input"
              />
            )}
          />
          <recoveryForm.Subscribe
            selector={(state) => [state.canSubmit, state.isSubmitting]}
            children={([canSubmit, isSubmitting]) => (
              <Button
                type="submit"
                disabled={!canSubmit || isSubmitting}
                className="w-full"
                data-testid="step-up-recovery-submit-button"
              >
                {isSubmitting ? "Verifying…" : "Use recovery code"}
              </Button>
            )}
          />
        </form>
      )}

      {mode !== "picker" && (
        <div className="text-center">
          <button
            type="button"
            onClick={() => {
              setError(null);
              setOtpSent(false);
              setMode("picker");
            }}
            className="text-sm text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
            data-testid="step-up-change-method-button"
          >
            ← Choose a different method
          </button>
        </div>
      )}
    </div>
  );
}
